import { __, compose, filter, head, keys, last, prop, sort, startsWith } from 'ramda'

export const getPlaceLevelId = place => {
  return compose(
    prop('id'), //
    prop(__, place),
    getLastPlaceLevelKey
  )(place)
}

export const getLastPlaceLevelKey = place => {
  return compose(
    last,
    sort((a, b) => a > b),
    filter(startsWith('placeLevel')),
    keys //
  )(place)
}

export const getFirstPlaceLevelKey = place => {
  return compose(
    head,
    sort((a, b) => a > b),
    filter(startsWith('placeLevel')),
    keys //
  )(place)
}

// ---------------------------------------------------------------------------
// Nível do local na hora de salvar
//
// Alguns locais (ex.: importados por planilha) têm um nível "fantasma" no fim da
// hierarquia: um nível sem nome, que não aparece na tela de edição (o último nível
// da tela é o próprio nome do local). Esse nível continua dentro do formulário e,
// por ser o último, ganhava de qualquer mudança feita em Cliente/Identificador:
// o local era salvo de novo no mesmo nível antigo e a alteração se perdia.
//
// Regras abaixo:
// - só níveis com nome (os que a pessoa vê e escolhe na tela) contam para o nível novo;
// - se a pessoa não mexeu nos níveis, mantém o nível original do local (não mexe em
//   segmentos nem em outros vínculos desse nível);
// - se mexeu, o nível salvo é o mais profundo entre os níveis escolhidos.
// ---------------------------------------------------------------------------

const PLACE_LEVEL_KEY = /^placeLevel(\d+)$/

const hasLevelName = level => level?.name !== undefined && level?.name !== null && String(level.name).trim() !== ''

const sortedIds = levels => levels.map(level => Number(level.id)).sort((a, b) => a - b)

// Chaves `placeLevel<levelId>` do formulário, em ordem numérica (do nível mais alto para o mais baixo).
export const getPlaceLevelKeys = form =>
  Object.keys(form || {})
    .filter(key => PLACE_LEVEL_KEY.test(key))
    .sort((a, b) => Number(a.match(PLACE_LEVEL_KEY)[1]) - Number(b.match(PLACE_LEVEL_KEY)[1]))

// Níveis com nome que a pessoa realmente escolheu (ignora vazios e níveis fantasma).
export const getSelectedPlaceLevels = form =>
  getPlaceLevelKeys(form)
    .map(key => form[key])
    .filter(level => !!level?.id && hasLevelName(level))

// Foto da hierarquia do local no momento em que o formulário abriu (vem de `placeLevelsResume`).
export const buildPlaceLevelsSnapshot = placeLevelsResume => {
  const levels = (placeLevelsResume || []).filter(level => !!level?.id)
  const leaf = levels.reduce((deepest, level) => (!deepest || level.levelId > deepest.levelId ? level : deepest), null)
  return { leafId: leaf?.id, namedIds: sortedIds(levels.filter(hasLevelName)) }
}

export const getPlaceLevelIdToSave = form => {
  const selected = getSelectedPlaceLevels(form)
  const deepest = selected[selected.length - 1]
  const snapshot = form?.__originalPlaceLevels

  const unchanged =
    !!snapshot?.leafId && !!deepest && JSON.stringify(sortedIds(selected)) === JSON.stringify(snapshot.namedIds || [])

  return unchanged ? snapshot.leafId : deepest?.id
}
